import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Settings, Plus, X } from 'lucide-react';

interface Skill {
  id: string;
  name: string;
  category: string;
  level: 'Beginner' | 'Intermediate' | 'Advanced' | 'Expert';
}

interface Language {
  id: string;
  name: string;
  proficiency: 'Basic' | 'Intermediate' | 'Advanced' | 'Fluent' | 'Native';
}

interface SkillsFormProps {
  data: Skill[];
  languages: Language[];
  onChange: (data: Skill[]) => void;
  onLanguagesChange: (data: Language[]) => void;
}

const skillCategories = [
  'Programming Languages',
  'Frameworks & Libraries',
  'Databases',
  'Tools & Technologies',
  'Soft Skills',
  'Languages',
  'Certifications',
  'Other'
];

const skillLevels = ['Beginner', 'Intermediate', 'Advanced', 'Expert'] as const;

const SkillsForm: React.FC<SkillsFormProps> = ({ data, languages, onChange, onLanguagesChange }) => {
  const [newSkill, setNewSkill] = useState({
    name: '',
    category: 'Programming Languages',
    level: 'Intermediate' as const
  });

  const addSkill = () => {
    if (newSkill.name.trim()) {
      const skill: Skill = {
        id: Date.now().toString(),
        name: newSkill.name.trim(),
        category: newSkill.category,
        level: newSkill.level
      };
      onChange([...data, skill]);
      setNewSkill({
        name: '',
        category: 'Programming Languages',
        level: 'Intermediate'
      });
    }
  };

  const removeSkill = (id: string) => {
    onChange(data.filter(skill => skill.id !== id));
  };

  const updateSkill = (id: string, field: keyof Skill, value: string) => {
    onChange(data.map(skill =>
      skill.id === id ? { ...skill, [field]: value } : skill
    ));
  };

  const groupedSkills = data.reduce((acc, skill) => {
    if (!acc[skill.category]) {
      acc[skill.category] = [];
    }
    acc[skill.category].push(skill);
    return acc;
  }, {} as Record<string, Skill[]>);

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'Beginner': return 'bg-gray-100 text-gray-800';
      case 'Intermediate': return 'bg-blue-100 text-blue-800';
      case 'Advanced': return 'bg-green-100 text-green-800';
      case 'Expert': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const [newLanguage, setNewLanguage] = useState<Language>({ id: '', name: '', proficiency: 'Intermediate' });

  const languageLevels = ['Basic', 'Intermediate', 'Advanced', 'Fluent', 'Native'] as const;

  const addLanguage = () => {
    if (newLanguage.name.trim()) {
      const item: Language = {
        id: Date.now().toString(),
        name: newLanguage.name.trim(),
        proficiency: newLanguage.proficiency
      };
      onLanguagesChange([...(languages || []), item]);
      setNewLanguage({ id: '', name: '', proficiency: 'Intermediate' });
    }
  };

  const removeLanguage = (id: string) => {
    onLanguagesChange((languages || []).filter(l => l.id !== id));
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Settings className="h-5 w-5" />
          Skills
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Add New Skill */}
        <div className="border rounded-lg p-4 space-y-4">
          <h3 className="text-lg font-medium">Add New Skill</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label htmlFor="skillName">Skill Name</Label>
              <Input
                id="skillName"
                placeholder="React, Python, Leadership..."
                value={newSkill.name}
                onChange={(e) => setNewSkill({ ...newSkill, name: e.target.value })}
                onKeyPress={(e) => e.key === 'Enter' && addSkill()}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="skillCategory">Category</Label>
              <Select
                value={newSkill.category}
                onValueChange={(value) => setNewSkill({ ...newSkill, category: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {skillCategories.map(category => (
                    <SelectItem key={category} value={category}>
                      {category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="skillLevel">Proficiency Level</Label>
              <Select
                value={newSkill.level}
                onValueChange={(value) => setNewSkill({ ...newSkill, level: value as typeof newSkill.level })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {skillLevels.map(level => (
                    <SelectItem key={level} value={level}>
                      {level}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button onClick={addSkill} className="w-full">
                <Plus className="h-4 w-4 mr-2" />
                Add Skill
              </Button>
            </div>
          </div>
        </div>

        {/* Skills by Category */}
        {Object.keys(groupedSkills).length > 0 ? (
          <div className="space-y-6">
            {Object.entries(groupedSkills).map(([category, skills]) => (
              <div key={category} className="space-y-3">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">{category}</h3>
                <div className="flex flex-wrap gap-2">
                  {skills.map(skill => (
                    <div
                      key={skill.id}
                      className={`inline-flex items-center gap-2 pl-3 pr-1.5 py-1.5 border rounded-full shadow-sm hover:shadow-md transition-all ${getLevelColor(skill.level)} bg-opacity-10 border-current`}
                    >
                      <span className="text-sm font-bold truncate max-w-[150px]">{skill.name}</span>
                      <div className="flex items-center gap-1.5">
                        <Badge variant="outline" className="text-[10px] px-1.5 h-4 uppercase font-bold border-current opacity-70">
                          {skill.level}
                        </Badge>
                        <button
                          type="button"
                          onClick={() => removeSkill(skill.id)}
                          className="hover:scale-110 transition-transform p-0.5 rounded-full hover:bg-white/20"
                          title="Remove skill"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-xl border-2 border-dashed">
            <Settings className="h-12 w-12 mx-auto mb-4 opacity-30" />
            <p className="font-medium">No skills added yet.</p>
            <p className="text-sm opacity-70">Add your first skill using the form above.</p>
          </div>
        )}

        {/* Removed redundant Skills Preview section */}

        {/* Languages */}
        <div className="border-t pt-6 space-y-4">
          <h3 className="text-lg font-medium">Languages</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="languageName">Language</Label>
              <Input
                id="languageName"
                placeholder="English, Nepali, Hindi..."
                value={newLanguage.name}
                onChange={(e) => setNewLanguage({ ...newLanguage, name: e.target.value })}
                onKeyPress={(e) => e.key === 'Enter' && addLanguage()}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="languageLevel">Proficiency</Label>
              <Select
                value={newLanguage.proficiency}
                onValueChange={(value) => setNewLanguage({ ...newLanguage, proficiency: value as Language['proficiency'] })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {languageLevels.map(level => (
                    <SelectItem key={level} value={level}>{level}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button onClick={addLanguage} className="w-full">
                <Plus className="h-4 w-4 mr-2" />
                Add Language
              </Button>
            </div>
          </div>

          {languages && languages.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {languages.map(lang => (
                <Badge key={lang.id} variant="outline" className="px-3 py-1">
                  {lang.name}
                  <span className="ml-1 text-xs text-gray-500">({lang.proficiency})</span>
                  <button
                    type="button"
                    onClick={() => removeLanguage(lang.id)}
                    className="ml-2 text-red-600 hover:text-red-700"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          ) : (
            <div className="text-sm text-gray-500">No languages added yet.</div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default SkillsForm;
